import React from 'react'
import type { CoverageSummaryItem } from '@/services/api/teacherPriority'
import type { BasicCascadePreviewResponse } from '@/types/teacher-request'
import { TeacherBadge } from './TeacherBadge'
import { AffinityMarks } from './AffinityMarks'
import { CascadeLegendTrigger } from './CascadeLegendTrigger'
import { getCascadeTeacherBadgeStatus, isFinalRequestStatus } from '@/utils/statusHelpers'

interface PriorityCascadeIndicatorProps {
  teachers: CoverageSummaryItem[]
  cascadeStatus?: string | null
  requestStatus?: string | null
  requestMode?: string
  requestFlow?: string
  preview?: BasicCascadePreviewResponse | null
  maxBadges?: number
  isResolved?: boolean
}

type PreviewTeacher = BasicCascadePreviewResponse['current_teachers'][0]

const getStatusColor = (status: string | null | undefined) => {
  switch (status) {
    case 'in_progress': return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
    case 'accepted': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    case 'filled': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    case 'declined': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    case 'partially_accepted': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    case 'timed_out': return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
    case 'no_substitute_found': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    case 'cancelled': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    case 'expired': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }
}

const getStatusLabel = (status: string | null | undefined) => {
  switch (status) {
    case 'in_progress': return 'In Bearbeitung'
    case 'accepted': return 'Akzeptiert'
    case 'filled': return 'Besetzt'
    case 'declined': return 'Abgelehnt'
    case 'partially_accepted': return 'Teilweise akzeptiert'
    case 'timed_out': return 'Noch offen'
    case 'no_substitute_found': return 'Kein Vertreter gefunden'
    case 'cancelled': return 'Storniert'
    case 'expired': return 'Abgelaufen'
    default: return 'Unbekannt'
  }
}

export function PriorityCascadeIndicator({
  teachers, cascadeStatus, requestStatus, requestMode: _requestMode, requestFlow, preview, maxBadges = 0, isResolved = false,
}: PriorityCascadeIndicatorProps) {
  // Finaler Request-Status dominiert das Kaskaden-Aggregat (Drift-Fix).
  const effectiveStatus = isFinalRequestStatus(requestStatus) ? requestStatus : cascadeStatus
  // Build coverage map from BOTH current AND next teachers (teacher_codes are unique)
  const coverageByCode = new Map<string, PreviewTeacher>()
  if (preview?.current_teachers) {
    for (const pt of preview.current_teachers) coverageByCode.set(pt.teacher_code, pt)
  }
  if (preview?.next_teachers) {
    for (const nt of preview.next_teachers) coverageByCode.set(nt.teacher_code, nt)
  }

  // Determine display teachers:
  // - If preview loaded: use preview.current_teachers (richest data)
  // - Else: use coverage.teachers (basic)
  const previewTeachers = preview?.current_teachers || []

  interface DisplayTeacher {
    id: string
    code: string
    status: string
    rank: number
    /** cascade-v2: automatisch vom Matcher ergänzt (nicht vom Anfragenden gewählt) */
    autoMatched: boolean
    /** cascade-v2: echter Name (nur Requester-/Admin-View; Lehrer-View maskiert) */
    name?: string | null
    contactedAt?: string | null
    origin?: string | null
  }

  let displayTeachers: DisplayTeacher[]

  if (previewTeachers.length > 0) {
    // cascade-v2 (B1): deterministische Sortierung — Wizard-Ränge vorne (1,2,3…),
    // danach automatisch ergänzte (matcher) chronologisch nach Kontaktzeitpunkt.
    // Backend sortiert bereits (A3); das ist die defensive Frontend-Seite desselben Keys.
    const wizardFirst = [...previewTeachers].sort((a, b) => {
      const aWizard = a.origin === 'wizard'
      const bWizard = b.origin === 'wizard'
      const aContact = a.contacted_at || '9999-12-31T23:59:59'
      const bContact = b.contacted_at || '9999-12-31T23:59:59'
      const aRank = a.priority_rank ?? 9999
      const bRank = b.priority_rank ?? 9999
      if (aWizard && bWizard) return aRank - bRank || aContact.localeCompare(bContact)
      if (aWizard !== bWizard) return aWizard ? -1 : 1
      return aContact.localeCompare(bContact) || aRank - bRank
    })
    displayTeachers = wizardFirst.map(pt => ({
      id: pt.teacher_code,
      code: pt.teacher_code,
      status: getCascadeTeacherBadgeStatus(pt.contact_status || 'contacted', requestStatus),
      rank: pt.priority_rank || 1,
      autoMatched: pt.origin === 'matcher',
      name: pt.teacher_name,
      contactedAt: pt.contacted_at,
      origin: pt.origin,
    }))
  } else {
    // Fallback ohne Preview — next-teachers nicht verfügbar.
    // Zeige ALLE Lehrer inkl. abgelehnte/abgesagte (Historie).
    // Deduplicate by teacher_id (same teacher can appear in multiple blocks)
    const sorted = [...teachers].sort((a, b) => a.priority_rank - b.priority_rank)
    const seen = new Set<string>()
    displayTeachers = sorted
      .filter(t => {
        if (seen.has(t.teacher_id)) return false
        seen.add(t.teacher_id)
        return true
      })
      .map(t => ({
        id: t.teacher_id,
        code: t.teacher_short_code || 'Unbek.',
        status: getCascadeTeacherBadgeStatus(t.status, requestStatus),
        rank: t.priority_rank,
        autoMatched: false,
      }))
  }

  if (displayTeachers.length === 0) return null

  // maxBadges applies ONLY to current teachers, NOT next.
  // cascade-v2 (B4): Badges mit Live-Status (kontaktiert/abgelaufen/Teilannahme/
  // angenommen) werden NIEMALS weggeschnitten — sonst könnte der aktuell
  // angeschriebene Lehrer unsichtbar werden, während graue Pending-Badges bleiben.
  // prionew-v2: recontacted (ausstehende zweite Anfragerunde) ebenfalls live.
  const LIVE_STATUSES = new Set(['contacted', 'timed_out', 'waiting', 'accepted', 'recontacted'])
  let currentDisplay: DisplayTeacher[]
  if (maxBadges > 0 && requestStatus !== 'cancelled') {
    const live = displayTeachers.filter(t => LIVE_STATUSES.has(t.status))
    const others = displayTeachers.filter(t => !LIVE_STATUSES.has(t.status))
    currentDisplay = [...live, ...others.slice(0, Math.max(0, maxBadges - live.length))]
  } else {
    currentDisplay = displayTeachers
  }

  const nextTeachers = preview?.next_teachers || []
  // Show next teachers only if there are any AND request is not resolved/accepted/filled
  const showNext = nextTeachers.length > 0 && !isResolved

  let label = 'Prioritätskaskade'
  if (requestFlow === 'automatic') label = 'Automatische Kaskade'

  const ariaLabel = `Kaskade: ${currentDisplay.map(t => t.code).join(', ')}`

  return (
    <div className="mt-3" role="img" aria-label={ariaLabel}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 inline-flex items-center gap-1">
          {label}
          <CascadeLegendTrigger />
        </span>
        {effectiveStatus && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(effectiveStatus)}`}>
            {getStatusLabel(effectiveStatus)}
          </span>
        )}
      </div>

      {/* All teachers in one row */}
      <div className="flex items-center gap-2 flex-wrap" role="list">
        {currentDisplay.map((teacher, index) => {
          const pt = coverageByCode.get(teacher.code)
          return (
            <React.Fragment key={teacher.id}>
              <div role="listitem" className="flex items-center gap-1">
                <TeacherBadge
                  teacherCode={teacher.code}
                  status={teacher.status}
                  priorityRank={teacher.rank}
                  auto_matched={teacher.autoMatched}
                  teacher_name={teacher.name}
                  coverageInfo={pt ? {
                    lessonCount: pt.lesson_count,
                    coverageScore: pt.coverage_score,
                    affinityBonus: pt.affinity_bonus,
                    coveredLessons: pt.covered_lessons || [],
                    missingLessons: pt.missing_lessons || [],
                  } : undefined}
                />
                <AffinityMarks classReason={pt?.class_reason} subjectReason={pt?.subject_reason} />
              </div>
              {(index < currentDisplay.length - 1 || showNext) && (
                <div className="flex-1 h-0.5 bg-gray-300 dark:bg-gray-600 min-w-[8px]" />
              )}
            </React.Fragment>
          )
        })}
        {showNext && nextTeachers.map((nt, i) => (
          <React.Fragment key={`next-${nt.teacher_code}-${i}`}>
            <div role="listitem" className="flex items-center gap-1">
                <TeacherBadge
                  teacherCode={nt.teacher_code}
                  status="pending"
                  priorityRank={nt.priority_rank}
                  is_next={true}
                coverageInfo={{
                  lessonCount: nt.lesson_count,
                  coverageScore: nt.coverage_score,
                  affinityBonus: nt.affinity_bonus,
                  coveredLessons: nt.covered_lessons || [],
                  missingLessons: nt.missing_lessons || [],
                }}
              />
              <AffinityMarks classReason={nt.class_reason} subjectReason={nt.subject_reason} />
            </div>
            {i < nextTeachers.length - 1 && (
              <div className="flex-1 h-0.5 bg-gray-200 dark:bg-gray-700 min-w-[8px]" />
            )}
          </React.Fragment>
        ))}
      </div>
      {showNext && preview?.next_teachers_total_count && preview.next_teachers_total_count > nextTeachers.length && (
        <p className="text-xs text-gray-400 mt-1">+ weitere</p>
      )}
    </div>
  )
}
